import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './lib/hooks/use-auth';
import { WalletProvider } from './components/WalletProvider';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./routes/page'));
const AboutPage = lazy(() => import('./routes/about/page'));
const DashboardPage = lazy(() => import('./routes/dashboard/page'));
const AchievementsPage = lazy(() => import('./routes/achievements/page'));

// Grants (public catalog + project check-ins)
const GrantsCatalogPage = lazy(() => import('./routes/grants/page'));
const GrantsDetailPage = lazy(() => import('./routes/grants/[slug]/page'));
const GrantsCheckinPage = lazy(() => import('./routes/grants/[slug]/checkin/page'));

// Academy
const AcademyPage = lazy(() => import('./routes/academy/page'));
// Academy courses (semester-based programs) - using classes pages which have been updated for courses
const AcademyCoursesPage = lazy(() => import('./routes/academy/classes/page')); // Redirects to /academy
const AcademyCourseDetailPage = lazy(() => import('./routes/academy/classes/[id]/page'));
const AcademyCourseEnrollPage = lazy(() => import('./routes/academy/classes/[id]/enroll/page'));
const AcademyCoursePage = lazy(() => import('./routes/academy/courses/[id]/page')); // For lesson view
const AcademyLessonPage = lazy(() => import('./routes/academy/courses/[id]/[moduleId]/[lessonId]/page'));
const AcademyProjectsPage = lazy(() => import('./routes/academy/projects/page'));
const AcademyProjectPublicPage = lazy(() => import('./routes/academy/projects/[id]/page'));
const AcademyProjectDashboardPage = lazy(() => import('./routes/academy/projects/[id]/dashboard/page'));
const AcademyNewProjectPage = lazy(() => import('./routes/academy/projects/new/page'));
const AcademyTeamsPage = lazy(() => import('./routes/academy/teams/page'));
const AcademyTeamDetailPage = lazy(() => import('./routes/academy/teams/[id]/page'));
const AcademyNewTeamPage = lazy(() => import('./routes/academy/teams/new/page'));
const AcademyTeamInvitePage = lazy(() => import('./routes/academy/teams/[id]/invite/page'));
const AcademyTeamJoinPage = lazy(() => import('./routes/academy/teams/join/[code]/page'));

// Admin
const AdminLayout = lazy(() => import('./components/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminPage = lazy(() => import('./routes/admin/page'));
const AdminClassesPage = lazy(() => import('./routes/admin/classes/page'));
const AdminCoursesPage = lazy(() => import('./routes/admin/courses/page'));
const AdminMissionsPage = lazy(() => import('./routes/admin/missions/page'));
const AdminMentorsPage = lazy(() => import('./routes/admin/mentors/page'));
const AdminWorkshopsPage = lazy(() => import('./routes/admin/workshops/page'));
const AdminWorkshopQRPage = lazy(() => import('./routes/admin/workshops/[id]/qr/page'));
const AdminWorkshopAttendancePage = lazy(() => import('./routes/admin/workshops/[id]/attendance/page'));
const AdminAdvertisementsPage = lazy(() => import('./routes/admin/advertisements/page'));
const AdminUsersPage = lazy(() => import('./routes/admin/users/page'));
const AdminMessagesPage = lazy(() => import('./routes/admin/messages/page'));
const AdminCvPage = lazy(() => import('./routes/admin/cv/page'));
const AdminAchievementsPage = lazy(() => import('./routes/admin/achievements/page'));
const AdminRafflePage = lazy(() => import('./routes/admin/raffle/page'));
const AdminP2PAnalyticsPage = lazy(() => import('./routes/admin/p2p-analytics/page'));
const AdminStreamViewersPage = lazy(() => import('./routes/admin/stream-viewers/page'));
const AdminAcademiesPage = lazy(() => import('./routes/admin/academies/page'));

// Auth
const SignInPage = lazy(() => import('./routes/auth/sign-in/page'));
const SignUpPage = lazy(() => import('./routes/auth/sign-up/page'));
const VerifyEmailPage = lazy(() => import('./routes/auth/verify-email/page'));
const ResetPasswordPage = lazy(() => import('./routes/auth/reset-password/page'));
const UnsubscribePage = lazy(() => import('./routes/unsubscribe/page'));

// Onboarding
const OnboardingPage = lazy(() => import('./routes/onboarding/page'));

// Class (single session) - route uses online-classroom component

// Courses
const CoursesPage = lazy(() => import('./routes/courses/page'));
const CourseDetailPage = lazy(() => import('./routes/courses/[id]/page'));
const LessonPage = lazy(() => import('./routes/courses/[id]/[moduleId]/[lessonId]/page'));

// Jobs (public board + staff tools under /mentor/jobs)
const JobsPage = lazy(() => import('./routes/jobs/page'));
const JobDetailPage = lazy(() => import('./routes/jobs/[slug]/page'));
const MentorJobsDashboardPage = lazy(() => import('./routes/mentor/jobs/page'));
const MentorJobNewPage = lazy(() => import('./routes/mentor/jobs/new/page'));
const MentorJobEditPage = lazy(() => import('./routes/mentor/jobs/[id]/edit/page'));
const MentorJobApplicationsPage = lazy(() => import('./routes/mentor/jobs/applications/page'));

// Companies
const CompaniesPage = lazy(() => import('./routes/companies/page'));
const CompanyDetailPage = lazy(() => import('./routes/companies/[id]/page'));
const CreateCompanyPage = lazy(() => import('./routes/companies/new/page'));
const ManageCompanyPage = lazy(() => import('./routes/companies/[id]/manage/page'));

// Workshops
const WorkshopsPage = lazy(() => import('./routes/workshops/page'));
const WorkshopDetailPage = lazy(() => import('./routes/workshops/[id]/page'));

// Missions pages removed for MVP

// Instructors
const InstructorsPage = lazy(() => import('./routes/instructors/page'));
const InstructorDetailPage = lazy(() => import('./routes/instructors/[id]/page'));

// Mentor
const MentorPage = lazy(() => import('./routes/mentor/page'));
const MentorCoursesPage = lazy(() => import('./routes/mentor/courses/page'));
const MentorStudentsPage = lazy(() => import('./routes/mentor/students/page'));
const MentorSubmissionsPage = lazy(() => import('./routes/mentor/submissions/page'));
const MentorStreamPage = lazy(() => import('./routes/mentor/stream/page'));
const MentorRecordingsPage = lazy(() => import('./routes/mentor/recordings/page'));
const MentorChatPage = lazy(() => import('./routes/mentor-chat/page'));

const MentorAcademiesPage = lazy(() => import('./routes/mentor/academies/page'));
const MeHubPage = lazy(() => import('./routes/me/page'));
const AcademyV2Shell = lazy(() => import('./routes/a/[slug]/shell'));
const AcademyV2CommunityTab = lazy(() => import('./routes/a/[slug]/tabs/community'));
const AcademyV2ClassroomTab = lazy(() => import('./routes/a/[slug]/tabs/classroom'));
const AcademyV2CalendarTab = lazy(() => import('./routes/a/[slug]/tabs/calendar'));
const AcademyV2MembersTab = lazy(() => import('./routes/a/[slug]/tabs/members'));
const AcademyV2JoinTab = lazy(() => import('./routes/a/[slug]/tabs/join'));
const AcademySettingsLayout = lazy(() => import('./routes/a/[slug]/settings/layout'));
const AcademySettingsGeneral = lazy(() => import('./routes/a/[slug]/settings/general'));
const AcademySettingsProducts = lazy(() => import('./routes/a/[slug]/settings/products'));
const AcademySettingsStaff = lazy(() => import('./routes/a/[slug]/settings/staff'));
const AcademySettingsCourses = lazy(() => import('./routes/a/[slug]/settings/courses'));
const AcademyLivePage = lazy(() => import('./routes/a/[slug]/live/[eventId]/page'));
const AcademyClassroomCoursePage = lazy(() => import('./routes/a/[slug]/classroom/[courseId]/page'));
const AcademyClassroomLessonBridgePage = lazy(() => import('./routes/a/[slug]/classroom/[courseId]/[moduleId]/[lessonId]/page'));

// Public mentor class stream
const PublicMentorStreamPage = lazy(() => import('./routes/live/[username]/page'));

// Playback
const PlaybackPage = lazy(() => import('./routes/playback/page'));

// Profile
const ProfilePage = lazy(() => import('./routes/profile/page'));
const UserProfilePage = lazy(() => import('./routes/profile/[userId]/page'));
const CvPage = lazy(() => import('./routes/cv/[username]/page'));

// Social & Messages pages removed for MVP
const NotificationsPage = lazy(() => import('./routes/notifications/page'));

// Classes
const MyClassesPage = lazy(() => import('./routes/my-classes/page'));
const OnlineClassroomPage = lazy(() => import('./routes/online-classroom/[sessionId]/page'));

// Weekend Class Analytics
const WeekendClass1Page = lazy(() => import('./routes/weekendclass1/page'));
const WeekendClass2Page = lazy(() => import('./routes/weekendclass2/page'));
const WeekendClass3Page = lazy(() => import('./routes/weekendclass3/page'));
const WeekendClass4Page = lazy(() => import('./routes/weekendclass4/page'));
const WeekendClass5Page = lazy(() => import('./routes/weekendclass5/page'));

// Series
const SeriesPage = lazy(() => import('./routes/series/[seriesId]/page'));

// Other
const FaucetPage = lazy(() => import('./routes/faucet/page'));
const RafflePage = lazy(() => import('./routes/raffle/page'));
const CheckinPage = lazy(() => import('./routes/checkin/[token]/page'));
const ContactPage = lazy(() => import('./routes/contact/page'));
const PrivacyPage = lazy(() => import('./routes/privacy/page'));
const TermsPage = lazy(() => import('./routes/terms/page'));
const NotFoundPage = lazy(() => import('./routes/not-found'));

// Redirects /profile → /profile/:username (or /profile/:id as fallback)
function MyProfileRedirect() {
  const { user } = useAuth();
  return <Navigate to={`/profile/${user?.username || user?.id || ''}`} replace />;
}

// Redirects /profile/edit → /profile/:username/edit for consistent URLs
function ProfileEditRedirect() {
  const { user } = useAuth();
  return <Navigate to={`/profile/${user?.username || user?.id || ''}/edit`} replace />;
}

// Redirect legacy /reset-password links to /auth/reset-password
function ResetPasswordRedirect() {
  const location = useLocation();
  return <Navigate to={`/auth/reset-password${location.search}`} replace />;
}

/** Cutover: legacy spec path `/academy/:slug/*` → implemented `/a/:slug/*`. */
function LegacyAcademyV2Redirect() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const s = slug ? decodeURIComponent(slug) : '';
  const suffix = location.pathname.replace(/^\/academy\/[^/]+/i, '') || '/community';
  return <Navigate to={`/a/${encodeURIComponent(s)}${suffix}`} replace />;
}

// Wrapper: only allow editing own profile; redirect to view if username doesn't match
function ProfileEditGuard({ children }: { children: React.ReactNode }) {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();
  const isOwnProfile = user && (username === user.username || username === user.id);
  if (!isOwnProfile && username) {
    return <Navigate to={`/profile/${username}`} replace />;
  }
  return <>{children}</>;
}

// Loading component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <WalletProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<Layout />}>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />

              {/* Grants/Projects */}
              <Route path="/projects" element={<GrantsCatalogPage />} />
              <Route path="/grants" element={<Navigate to="/projects" replace />} />
              <Route path="/grants/:slug" element={<GrantsDetailPage />} />
              <Route
                path="/projects/:slug/checkin"
                element={
                  <ProtectedRoute>
                    <GrantsCheckinPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/grants/:slug/checkin"
                element={
                  <ProtectedRoute>
                    <GrantsCheckinPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Auth routes */}
              <Route path="/auth/sign-in" element={<SignInPage />} />
              <Route path="/auth/sign-up" element={<SignUpPage />} />
              <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordRedirect />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              
              {/* Onboarding */}
              <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
              
              {/* Academy routes */}
              <Route path="/academy" element={<AcademyPage />} />
              <Route path="/academy/courses" element={<AcademyCoursesPage />} />
              <Route path="/academy/courses/:id" element={<AcademyCourseDetailPage />} />
              <Route path="/academy/courses/:id/enroll" element={<AcademyCourseEnrollPage />} />
              {/* Projects and Teams routes */}
              <Route path="/academy/teams" element={<AcademyTeamsPage />} />
              <Route path="/academy/teams/new" element={<ProtectedRoute><AcademyNewTeamPage /></ProtectedRoute>} />
              <Route path="/academy/teams/:id" element={<AcademyTeamDetailPage />} />
              <Route path="/academy/teams/:id/invite" element={<ProtectedRoute><AcademyTeamInvitePage /></ProtectedRoute>} />
              <Route path="/academy/teams/join/:code" element={<AcademyTeamJoinPage />} />
              {/* Business projects (canonical) */}
              <Route path="/projects/new" element={<ProtectedRoute><AcademyNewProjectPage /></ProtectedRoute>} />
              <Route path="/projects/:id" element={<AcademyProjectPublicPage />} />
              <Route
                path="/projects/:id/dashboard"
                element={
                  <ProtectedRoute>
                    <AcademyProjectDashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Legacy business projects redirects */}
              <Route path="/grants/projects" element={<Navigate to="/projects" replace />} />
              <Route path="/grants/projects/new" element={<Navigate to="/projects/new" replace />} />
              <Route path="/grants/projects/:id" element={<Navigate to="/projects/:id" replace />} />
              <Route path="/grants/projects/:id/dashboard" element={<Navigate to="/projects/:id/dashboard" replace />} />

              {/* Backwards-compatible redirects */}
              <Route path="/academy/projects" element={<Navigate to="/projects" replace />} />
              <Route path="/academy/projects/new" element={<Navigate to="/projects/new" replace />} />
              <Route path="/academy/projects/:id" element={<Navigate to="/projects/:id" replace />} />
              <Route path="/academy/projects/:id/dashboard" element={<Navigate to="/projects/:id/dashboard" replace />} />
              
              {/* Courses routes (semester-based programs) */}
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/courses/:id" element={<CourseDetailPage />} />
              <Route path="/courses/:id/:moduleId/:lessonId" element={<LessonPage />} />

              <Route path="/academy/:slug/*" element={<LegacyAcademyV2Redirect />} />

              <Route path="/a/:slug" element={<AcademyV2Shell />}>
                <Route index element={<Navigate to="community" replace />} />
                <Route path="community" element={<AcademyV2CommunityTab />} />
                <Route path="classroom" element={<AcademyV2ClassroomTab />} />
                <Route path="classroom/:courseId" element={<AcademyClassroomCoursePage />} />
                <Route
                  path="classroom/:courseId/:moduleId/:lessonId"
                  element={<AcademyClassroomLessonBridgePage />}
                />
                <Route path="calendar" element={<AcademyV2CalendarTab />} />
                <Route path="members" element={<AcademyV2MembersTab />} />
                <Route path="join" element={<AcademyV2JoinTab />} />
                <Route path="live/:eventId" element={<AcademyLivePage />} />
                <Route path="settings" element={<AcademySettingsLayout />}>
                  <Route index element={<Navigate to="general" replace />} />
                  <Route path="general" element={<AcademySettingsGeneral />} />
                  <Route path="products" element={<AcademySettingsProducts />} />
                  <Route path="staff" element={<AcademySettingsStaff />} />
                  <Route path="courses" element={<AcademySettingsCourses />} />
                </Route>
              </Route>

              {/* Class routes (single sessions) */}
              <Route path="/classroom/:id" element={<ProtectedRoute><OnlineClassroomPage /></ProtectedRoute>} />
              <Route path="/my-classes" element={<ProtectedRoute><MyClassesPage /></ProtectedRoute>} />
              
              {/* Jobs (public) */}
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/jobs/:slug" element={<JobDetailPage />} />

              {/* Companies routes */}
              <Route path="/companies" element={<CompaniesPage />} />
              <Route path="/companies/new" element={<ProtectedRoute><CreateCompanyPage /></ProtectedRoute>} />
              <Route path="/companies/:id" element={<CompanyDetailPage />} />
              <Route path="/companies/:id/manage" element={<ProtectedRoute><ManageCompanyPage /></ProtectedRoute>} />

              {/* Workshops routes */}
              <Route path="/workshops" element={<WorkshopsPage />} />
              <Route path="/workshops/:id" element={<WorkshopDetailPage />} />
              
              {/* Missions routes removed for MVP */}
              
              {/* Mentors routes (replaces instructors) */}
              <Route path="/mentors" element={<InstructorsPage />} />
              <Route path="/mentors/:id" element={<InstructorDetailPage />} />

              {/* Public mentor class URLs: /class/:username */}
              <Route path="/class/:username" element={<PublicMentorStreamPage />} />
              
              {/* Mentor routes */}
              <Route path="/mentor" element={<ProtectedRoute><MentorPage /></ProtectedRoute>} />
              <Route path="/mentor/academies" element={<ProtectedRoute><MentorAcademiesPage /></ProtectedRoute>} />
              <Route path="/mentor/courses" element={<ProtectedRoute><MentorCoursesPage /></ProtectedRoute>} />
              <Route path="/mentor/students" element={<ProtectedRoute><MentorStudentsPage /></ProtectedRoute>} />
              <Route path="/mentor/submissions" element={<ProtectedRoute><MentorSubmissionsPage /></ProtectedRoute>} />
              <Route path="/mentor/stream" element={<ProtectedRoute><MentorStreamPage /></ProtectedRoute>} />
              <Route path="/mentor/recordings" element={<ProtectedRoute><MentorRecordingsPage /></ProtectedRoute>} />
              <Route path="/mentor/jobs/applications" element={<ProtectedRoute><MentorJobApplicationsPage /></ProtectedRoute>} />
              <Route path="/mentor/jobs/new" element={<ProtectedRoute><MentorJobNewPage /></ProtectedRoute>} />
              <Route path="/mentor/jobs/:id/edit" element={<ProtectedRoute><MentorJobEditPage /></ProtectedRoute>} />
              <Route path="/mentor/jobs" element={<ProtectedRoute><MentorJobsDashboardPage /></ProtectedRoute>} />
              <Route path="/mentor-chat" element={<MentorChatPage />} />

              {/* Playback - for mentors and admins to view recordings */}
              <Route path="/playback" element={<ProtectedRoute><PlaybackPage /></ProtectedRoute>} />

              {/* Profile routes - order matters: more specific paths first */}
              <Route path="/profile" element={<ProtectedRoute><MyProfileRedirect /></ProtectedRoute>} />
              <Route path="/profile/edit" element={<ProtectedRoute><ProfileEditRedirect /></ProtectedRoute>} />
              <Route path="/profile/:username/edit" element={<ProtectedRoute><ProfileEditGuard><ProfilePage /></ProfileEditGuard></ProtectedRoute>} />
              <Route path="/profile/:username" element={<UserProfilePage />} />

              {/* CV public route */}
              <Route path="/cv/:username" element={<CvPage />} />
              
              {/* Social & Messages routes removed for MVP */}
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              
              {/* Faucet */}
              <Route path="/faucet" element={<ProtectedRoute><FaucetPage /></ProtectedRoute>} />

              {/* Raffle */}
              <Route path="/raffle" element={<RafflePage />} />

              {/* Dashboard & Achievements */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/me" element={<ProtectedRoute><MeHubPage /></ProtectedRoute>} />
              <Route path="/achievements" element={<AchievementsPage />} />
              
              {/* Admin routes - sidebar layout with all sections */}
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminPage />} />
                <Route path="classes" element={<AdminClassesPage />} />
                <Route path="courses" element={<AdminCoursesPage />} />
                <Route path="missions" element={<AdminMissionsPage />} />
                <Route path="mentors" element={<AdminMentorsPage />} />
                <Route path="workshops" element={<AdminWorkshopsPage />} />
                <Route path="workshops/:id/qr" element={<AdminWorkshopQRPage />} />
                <Route path="workshops/:id/attendance" element={<AdminWorkshopAttendancePage />} />
                <Route path="advertisements" element={<AdminAdvertisementsPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="messages" element={<AdminMessagesPage />} />
                <Route path="cv" element={<AdminCvPage />} />
                <Route path="achievements" element={<AdminAchievementsPage />} />
                <Route path="raffle" element={<AdminRafflePage />} />
                <Route path="p2p-analytics" element={<AdminP2PAnalyticsPage />} />
                <Route path="stream-viewers" element={<AdminStreamViewersPage />} />
                <Route path="academies" element={<AdminAcademiesPage />} />
              </Route>
              
              {/* Weekend Class Analytics */}
              <Route path="/weekendclass1" element={<WeekendClass1Page />} />
              <Route path="/weekendclass2" element={<WeekendClass2Page />} />
              <Route path="/weekendclass3" element={<WeekendClass3Page />} />
              <Route path="/weekendclass4" element={<WeekendClass4Page />} />
              <Route path="/weekendclass5" element={<WeekendClass5Page />} />

              {/* Series */}
              <Route path="/series/:seriesId" element={<SeriesPage />} />
              
              {/* Checkin */}
              <Route path="/checkin/:token" element={<CheckinPage />} />
              
              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      </WalletProvider>
    </AuthProvider>
  );
}
